import { useState, useCallback } from 'react';
import apiClient from '../lib/api/apiClient';
import { useMyLeagueStore } from '../store/myLeagueStore';

// Module-level cache for salaries to persist across component remounts
const salariesCache: { [leagueId: number]: any } = {};

export interface Deal {
  expires: string | number | Date;
  viewed_at: string | null;
  id: number;
  league_id: number;
  created: string;
  updated: string;
  created_by?: number;
  type: 'trade' | 'claim_waiver' | 'free_agent_offer';
  status: 'proposed' | 'approved' | 'expired' | 'rejected' | 'under_review' | 'withdrawn' | 'declined' | 'accepted' | 'countered' | 'league_review' | 'has_salary_issue' | 'completed' | 'accepting_offers' | 'canceled' | 'settlement_trade';
  original?: number;
  parent?: number;
  child?: number;
  note?: string;
  team1?: number;
  players1?: number[];
  team1_accepted: boolean;
  team2?: number;
  players2?: number[];
  team2_accepted: boolean;
  players1Details?: Array<{
    roster_id: number;
    player_id: number;
    first_name: string;
    last_name: string;
    positions: string[];
    // rwPlayer structure that TradeModal expects
    rwPlayer: {
      id: number;
      first_name: string;
      last_name: string;
      position_1: string;
      position_2?: string;
      position_3?: string;
      mlb_team?: string;
      image?: string;
      bats?: string;
      throws?: string;
    };
    playerContract?: {
      current_salary: number;
      contractYears?: { year: number; salary: number }[];
    };
    status?: string;
    level?: string;
    is_40_man?: boolean;
    team_id?: number;
    year?: number;
    trade_status?: string | null;
    trade_status_until?: string | null;
  }>;
  players2Details?: Array<{
    roster_id: number;
    player_id: number;
    first_name: string;
    last_name: string;
    positions: string[];
    // rwPlayer structure that TradeModal expects
    rwPlayer: {
      id: number;
      first_name: string;
      last_name: string;
      position_1: string;
      position_2?: string;
      position_3?: string;
      mlb_team?: string;
      image?: string;
      bats?: string;
      throws?: string;
    };
    playerContract?: {
      current_salary: number;
      contractYears?: { year: number; salary: number }[];
    };
    status?: string;
    level?: string;
    is_40_man?: boolean;
    team_id?: number;
    year?: number;
    trade_status?: string | null;
    trade_status_until?: string | null;
  }>;
  protests?: Array<{
    teamId: number;
    note: string;
    created: string;
    createdBy: number;
    username: string;
  }>;
}

export interface CreateDealData {
  league_id: number;
  type: Deal['type'];
  status: Deal['status'];
  team1?: number;
  players1?: number[];
  team2?: number;
  players2?: number[];
  note?: string;
  original?: number;
  parent?: number;
  child?: number;
}

export interface DealFilters {
  statusList?: Deal['status'][];
  teamId?: number;
  playerId?: number;
  includePlayers?: boolean;
  type?: Deal['type'];
}

export interface DealHistoryOptions {
  limit?: number;
  offset?: number;
}

export interface DealHistoryResponse {
  deals: Deal[];
  total: number;
  limit: number;
  offset: number;
}

export interface ContractDetails {
  salary?: number;
  years?: number;
  contractType?: string;
  buyout?: number;
  [key: string]: unknown;
}

export const useDeals = (leagueId?: number) => {
  
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalDeals, setTotalDeals] = useState(0);
  // Initialize salaries from cache if available, otherwise null
  const [salaries, setSalaries] = useState<any>(leagueId && salariesCache[leagueId] ? salariesCache[leagueId] : null);

  // Get all deals for a league
  const getLeagueDeals = useCallback(async (filters?: DealFilters) => {
    if (!leagueId) {
      console.log('useDeals: No leagueId provided, skipping getLeagueDeals');
      return;
    }

    console.log('useDeals: Getting league deals with filters:', filters);
    
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams();
      if (filters?.teamId) queryParams.append('teamId', filters.teamId.toString());
      if (filters?.statusList) queryParams.append('statusList', JSON.stringify(filters.statusList));
      if (filters?.playerId) queryParams.append('playerId', filters.playerId.toString());
      if (filters?.includePlayers) queryParams.append('includePlayers', filters.includePlayers.toString());
      if (filters?.type) queryParams.append('type', filters.type);
      const url = `/deals/getLeagueDeals/${leagueId}?${queryParams}`;
      console.log('useDeals: Making API call to', url);
      const response = await apiClient.get(url);
      setDeals(response.data);
      setTotalDeals(response.data.length);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch deals';
      console.error('useDeals: Error fetching league deals', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  // Get a specific deal by ID
  const getDealById = useCallback(async (dealId: number): Promise<Deal | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get(`/deals/${dealId}`);
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch deal';
      setError(errorMessage);
      console.error('Error fetching deal:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new deal
  const createDeal = useCallback(async (dealData: CreateDealData): Promise<Deal | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post('/deals', { dealData });
      const newDeal = response.data;
      
      // Add the new deal to the current list
      setDeals(prev => [newDeal, ...prev]);
      setTotalDeals(prev => prev + 1);
      
      return newDeal;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create deal';
      setError(errorMessage);
      console.error('Error creating deal:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update deal data
  const updateDeal = useCallback(async (dealId: number, updateData: Partial<CreateDealData>, updatedByTeamId?: number): Promise<Deal | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.put(`/deals/${dealId}/update`, { 
        dealData: updateData, 
        updatedByTeamId 
      });
      const updatedDeal = response.data;
      
      // Update the deal in the current list
      setDeals(prev => prev.map(deal => 
        deal.id === dealId ? updatedDeal : deal
      ));
      
      return updatedDeal;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update deal';
      setError(errorMessage);
      console.error('Error updating deal:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);



  // Approve deal by commissioner (for accepted deals)
  const approveDealByCommissioner = useCallback(async (dealId: number, note?: string): Promise<Deal | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/deals/${dealId}/approve`, { note });
      const updatedDeal = response.data;
      
      // Update the deal in the current list
      setDeals(prev => prev.map(deal => 
        deal.id === dealId ? updatedDeal : deal
      ));
      
      return updatedDeal;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to approve deal by commissioner';
      setError(errorMessage);
      console.error('Error approving deal by commissioner:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Decline deal for a team (team declining the deal)
  const declineDealForTeam = useCallback(async (dealId: number, teamId: number, note?: string): Promise<Deal | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/deals/${dealId}/decline`, { teamId, note });
      const updatedDeal = response.data;
      
      // Update the deal in the current list
      setDeals(prev => prev.map(deal => 
        deal.id === dealId ? updatedDeal : deal
      ));
      
      return updatedDeal;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to decline deal';
      setError(errorMessage);
      console.error('Error declining deal:', err);
      throw err; // Re-throw the error so the calling component can handle it
    } finally {
      setLoading(false);
    }
  }, []);

  // Veto deal as commissioner (outright veto)
  const vetoDealAsCommissioner = useCallback(async (dealId: number, note?: string): Promise<Deal | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/deals/${dealId}/veto`, { note });
      const updatedDeal = response.data;
      
      // Update the deal in the current list
      setDeals(prev => prev.map(deal => 
        deal.id === dealId ? updatedDeal : deal
      ));
      
      return updatedDeal;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reject deal';
      setError(errorMessage);
      console.error('Error vetoing deal:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);



  // Get pending deals for a team
  const getPendingDealsForTeam = useCallback(async (teamId: number): Promise<Deal[]> => {
    if (!leagueId) return [];
    
    setLoading(true);
    setError(null);
    
    try {
      // Use the new getLeagueDeals endpoint with teamId filter
      const response = await apiClient.get(`/deals/getLeagueDeals/${leagueId}?teamId=${teamId}`);
      const pendingDeals = response.data;
      
      // Update the deals state with pending deals
      setDeals(pendingDeals);
      setTotalDeals(pendingDeals.length);
      
      return pendingDeals;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch pending deals';
      setError(errorMessage);
      console.error('Error fetching pending deals:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  // Mark deal as viewed by a team
  const viewedDeal = useCallback(async (dealId: number, teamId: number): Promise<Deal | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/deals/${dealId}/viewed`, { teamId });
      const updatedDeal = response.data;
      
      // Update the deal in the current list
      setDeals(prev => prev.map(deal => 
        deal.id === dealId ? updatedDeal : deal
      ));
      
      return updatedDeal;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to mark deal as viewed';
      setError(errorMessage);
      console.error('Error marking deal as viewed:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Withdraw a deal (only by the team that last proposed/countered)
  const withdrawDeal = useCallback(async (dealId: number, teamId: number, note?: string): Promise<Deal | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/deals/${dealId}/withdraw`, { teamId, note });
      const updatedDeal = response.data;
      
      // Update the deal in the current list
      setDeals(prev => prev.map(deal => 
        deal.id === dealId ? updatedDeal : deal
      ));
      
      return updatedDeal;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to withdraw deal';
      setError(errorMessage);
      console.error('Error withdrawing deal:', err);
      throw err; // Re-throw the error so the calling component can handle it
    } finally {
      setLoading(false);
    }
  }, []);

  // Agree to a deal (team accepting the deal as-is)
  const agreeToDeal = useCallback(async (dealId: number, note?: string): Promise<Deal | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/deals/${dealId}/agreeToDeal`, { note });
      const updatedDeal = response.data;
      
      // Update the deal in the current list
      setDeals(prev => prev.map(deal => 
        deal.id === dealId ? updatedDeal : deal
      ));
      
      return updatedDeal;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to agree to deal';
      setError(errorMessage);
      console.error('Error agreeing to deal:', err);
      throw err; // Re-throw the error so the calling component can handle it
    } finally {
      setLoading(false);
    }
  }, []);

  // Protest a deal (team not involved in trade protesting it)
  const protestDeal = useCallback(async (dealId: number, teamId: number, note: string): Promise<Deal | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/deals/protest-deal/${dealId}`, { teamId, note });
      const updatedDeal = response.data;
      
      // Update the deal in the current list
      setDeals(prev => prev.map(deal => 
        deal.id === dealId ? updatedDeal : deal
      ));
      
      return updatedDeal;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to protest deal';
      setError(errorMessage);
      console.error('Error protesting deal:', err);
      throw err; // Re-throw the error so the calling component can handle it
    } finally {
      setLoading(false);
    }
  }, []);

  // Get deal history for a league
  const getDealHistory = useCallback(async (options?: DealHistoryOptions): Promise<DealHistoryResponse | null> => {
    if (!leagueId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams();
      if (options?.limit) queryParams.append('limit', options.limit.toString());
      if (options?.offset) queryParams.append('offset', options.offset.toString());

      const response = await apiClient.get(`/deals/league/${leagueId}/history?${queryParams}`);
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch deal history';
      setError(errorMessage);
      console.error('Error fetching deal history:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  // Refresh deals (useful for polling or manual refresh)
  const refreshDeals = useCallback(() => {
    if (leagueId) {
      // Use the new endpoint directly
      apiClient.get(`/deals/getLeagueDeals/${leagueId}`).then(response => {
        setDeals(response.data);
        setTotalDeals(response.data.length);
      }).catch(err => {
        console.error('Error refreshing deals:', err);
      });
    }
  }, [leagueId]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear all deals (useful when changing leagues)
  const clearDeals = useCallback(() => {
    setDeals([]);
    setTotalDeals(0);
    setError(null);
  }, []);

  // Claim a waived player
  const claimWaiver = useCallback(async (playerId: number, teamId: number, onSuccess?: () => void) => {
    try {
      setLoading(true);
      setError(null);
      
      // Get leagueId from the hook
      const myLeague = useMyLeagueStore.getState().myLeague;
      if (!myLeague?.id) {
        throw new Error('No league selected');
      }
      
      const response = await apiClient.post(`/deals/claim_waiver/${playerId}`, {
        teamId,
        leagueId: myLeague.id
      });
      
      // Refresh deals after claiming
      await refreshDeals();
      
      // Call the success callback if provided (e.g., to refresh waiver wire data)
      if (onSuccess) {
        onSuccess();
      }
      
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to claim waiver';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [refreshDeals]);

  // Make a free agent offer
  const freeAgentOffer = useCallback(async (playerId: number, teamId: number, contractDetails: ContractDetails, onSuccess?: () => void) => {
    try {
      setLoading(true);
      setError(null);
      
      // Get leagueId from the hook
      const myLeague = useMyLeagueStore.getState().myLeague;
      if (!myLeague?.id) {
        throw new Error('No league selected');
      }
      
      const response = await apiClient.post(`/deals/free_agent_offer/${playerId}`, {
        teamId,
        leagueId: myLeague.id,
        contractDetails
      });
      
      // Refresh deals after making offer
      await refreshDeals();
      
      // Call the success callback if provided (e.g., to refresh free agents data)
      if (onSuccess) {
        onSuccess();
      }
      
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to make free agent offer';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [refreshDeals]);

  // Make a free agent offer
  const getSalariesForLeague = useCallback(async (onSuccess?: () => void) => {
    try {
      setLoading(true);
      setError(null);
      
      // Get leagueId from the hook
      const myLeague = useMyLeagueStore.getState().myLeague;
      if (!myLeague?.id) {
        throw new Error('No league selected');
      }
      
      const response = await apiClient.get(`/deals/getSalaries/${myLeague.id}`);
      
      // Store the salaries data in state and cache
      setSalaries(response.data);
      salariesCache[myLeague.id] = response.data;
      
      // Call the success callback if provided (e.g., to refresh free agents data)
      if (onSuccess) {
        onSuccess();
      }
      
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load salaries';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);


  return {
    // State
    deals,
    loading,
    error,
    totalDeals,
    salaries,
    
    // Actions
    getLeagueDeals,
    getDealById,
    createDeal,
    updateDeal,
    approveDealByCommissioner,
    declineDealForTeam,
    vetoDealAsCommissioner,
    getPendingDealsForTeam,
    viewedDeal,
    withdrawDeal,
    agreeToDeal,
    protestDeal,
    getDealHistory,
    refreshDeals,
    clearError,
    clearDeals,
    claimWaiver,
    freeAgentOffer,
    getSalariesForLeague,
  };
}; 